/**
 * Base Launch platform
 */

define(function(require, exports, module) {
    'use strict';

    var _ = require('underscore');
    var core = require('core');
    var url = require('util/url.util');
    var LauncherWindowView = require('modules/launcher/launcher.window.view');
    var config = require('config');
    var historyUtil = require('util/history.util');
    var device = require('util/device.util');
    const storage = require('util/storage.util');

    module.exports = {

        setLauncher: function(launcher) {
            this.launcher = launcher;
        },

        setAdditionalQueryString: function(queryString) {
            this.additionalQueryString = queryString;
        },

        /**
         * Generic URL using configured URL for game provider & added all url params to game launch URL.
         * @param launchUrl
         * @returns {String}
         */
        getGenericLaunchUrl: function(launchUrl) {
            var launchUrlSplit = launchUrl.split('?');
            var baseUrl = launchUrlSplit[0];
            var urlParams = launchUrlSplit.length === 2 && url.getURLParams(launchUrl) || {};
            var customUrlParams = _.omit(url.getURLParams(), ['real', 'back_url']);
            var mergedUrlParams = _.extend(urlParams, customUrlParams); // merge params by priority

            return baseUrl + '?' + url.serialize(mergedUrlParams);
        },

        /**
         * Add additional URL parameters to the end of launch URl, existing parameters will not changed
         * @param launchUrl
         * @return {String}
         */
        addAdditionalParamsToLaunchUrl: function(launchUrl) {
            var launchUrlSplit = launchUrl.split('?');
            var baseUrl = launchUrlSplit[0];
            var urlParams = launchUrlSplit[1];

            var buildUrl = function(params) {
                return [baseUrl, params].join('?');
            };

            var mergeParams = function(p1, p2) {
                return url
                    .serialize(_
                        .defaults(
                            url.getURLParams(p1),
                            url.getURLParams(p2)
                        )
                    );
            };

            return !this.additionalQueryString
                ? launchUrl
                : !urlParams
                    ? buildUrl(this.additionalQueryString)
                    : buildUrl(mergeParams(this.additionalQueryString, urlParams));
        },

        addUrlCustomParameters: function(launchUrl, options) {
            if (!options?.game) {
                return launchUrl;
            }

            const gameName = options.game.get('name');
            const urlCustomParameters = options.game.get('urlCustomParameters');

            if (!urlCustomParameters) {
                return launchUrl;
            }

            try {
                const resultUrl = new window.URL(launchUrl);
                const additionalUrlCustomParameters = new window.URLSearchParams(urlCustomParameters);

                additionalUrlCustomParameters.forEach((value, key) => {
                    resultUrl.searchParams.set(key, value);
                });

                return resultUrl.toString();
            } catch {
                core.error(`Game configuration error: check urlCustomParameters for game "${gameName}"`);
            }

            return launchUrl;
        },

        /**
         * Get configured launch method
         * @param {String} [gameType]
         * @return {String}
         */
        getLaunchOptions: function(gameType) {
            return _.find(config.get('gameTypes'), (item) => item.name === (gameType || this.provider));
        },

        /**
         * Open game with right launch method
         * @param  {Object} options
         */
        launch: function(options) {
            const preview = url.getLocationParameterByName('preview');

            options = _.extend(options, this.getLaunchOptions());

            if (options.launchUrl && preview != null) {
                options.launchUrl += (options.launchUrl.includes('?') ? '&' : '?') +
                    'preview=' + preview;
            }

            var launchMethod = device.isMobilePlatform
                ? options.launchMethodMobile
                : options.launchMethodDesktop;

            switch (launchMethod) {
                case 'redirectInThisWindow':
                    this.redirectLaunch(this.prepareRedirectUrl(options));
                    break;
                case 'wrapperInThisWindow':
                    this.redirectLaunch(this.prepareWrapperUrl(options));
                    break;
                case 'redirectInNewWindow':
                    this.launchInNewWindow(options, 'redirect');
                    break;
                case 'wrapperInNewWindow':
                    this.launchInNewWindow(options, 'wrapper');
                    break;
                default:
                    this.redirectLaunch(this.prepareRedirectUrl(options));
            }
        },

        navigateFromLaunchGamePath: function() {
            const gamesPageUrl = storage.session.getItem('gamesPageUrl');
            const { prevPath } = historyUtil.state?.state || {};

            if (url.isLaunchGamePath()) {
                storage.session.removeItem('gamesPageUrl');

                Playtech.API.common.replace(gamesPageUrl || prevPath || config.get('rootPage'));
            }
        },

        /**
         * [launchInNewWindow Open game in new window by redirect]
         * @param  {Object} [options]
         * @param  {String} [type]    [Method Launch Type]
         */
        launchInNewWindow: function(options, type) {
            const launchOptions = type === 'wrapper'
                ? this.prepareWrapperUrl(options)
                : this.prepareRedirectUrl(options);

            new LauncherWindowView(launchOptions);

            if (url.isLaunchGamePath()) {
                this.navigateFromLaunchGamePath();
            }
        },

        /**
         * Open game in current window
         * @param  {Object} options
         */
        redirectLaunch: function(options) {
            if (url.pathname.includes('/igaming/')) {
                historyUtil.replaceState(null, null, config.get('rootPage') || '/');
            }

            url.redirect(options.launchUrl);
        },

        /**
         * Prepare launch URL for redirect
         * @param  {Object} options
         * @return {Object}
         */
        prepareRedirectUrl: function(options) {
            let redirectUrl = options.launchUrl;

            redirectUrl = this.addUrlCustomParameters(redirectUrl, options); // add query string from CMS game config
            redirectUrl = this.getGenericLaunchUrl(redirectUrl); // add custom query string from URL
            redirectUrl = this.addAdditionalParamsToLaunchUrl(redirectUrl); // add query string from portlet config (have more priority)

            return {
                ...options,
                launchUrl: redirectUrl.toString()
            };
        },

        /**
         * Prepare launch URL for wrapper
         * @param  {Object} options
         * @return {Object}
         */
        prepareWrapperUrl: function(options) {
            const preview = url.getLocationParameterByName('preview') || '';
            const requestStamp = config.get('requestStamp');
            const wrapperUrl = new window.URL(`${url.origin}/${config.get('themeName')}/js/app-out/html/wrapper.html`);

            wrapperUrl.searchParams.append('urlType', options.urlType);
            wrapperUrl.searchParams.append('preview', preview);
            wrapperUrl.searchParams.append('additionalQueryString', this.additionalQueryString || '');
            wrapperUrl.searchParams.append('t', requestStamp);

            if (options?.game) {
                const gameName = options.game.get('name') || '';
                const gameCode = options.game.get('gameCode') || '';
                const mobileGameCode = options.game.get('mobileGameCode') || '';
                const gameType = options.game.get('gameType') || '';
                const integrationType = options.game.get('integrationType') || '';
                const liveLaunchAlias = options.game.get('liveLaunchAlias') || '';
                const urlCustomParameters = options.game.get('urlCustomParameters') || '';

                wrapperUrl.searchParams.append('gameName', gameName);
                wrapperUrl.searchParams.append('gameType', gameType);
                wrapperUrl.searchParams.append('game', gameCode);
                wrapperUrl.searchParams.append('gameCode', gameCode);
                wrapperUrl.searchParams.append('mobileGameCode', mobileGameCode || gameCode);
                wrapperUrl.searchParams.append('integrationType', integrationType);
                wrapperUrl.searchParams.append('liveLaunchAlias', liveLaunchAlias);
                wrapperUrl.searchParams.append('urlCustomParameters', urlCustomParameters);
            }

            return {
                ...options,
                launchUrl: wrapperUrl.toString()
            };
        }
    };
});
